import { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useAuthMessages } from "@/context/auth/AuthContext";
import {
  defaultInterpretEvent,
  payloadContainsAny,
  usePendingWorkflowsStore,
} from "@/context/pending-workflows/PendingWorkflowsContext";
import { useToast } from "./use-toast";

/**
 * Suscriptor global a mensajes del websocket. Por cada mensaje nuevo:
 *
 * 1. Filtra workflows pendientes cuyo `socketEventType === notification.type`.
 * 2. De esos, busca el que matchee por `correlationIds` contra el payload.
 *    Si ninguno matchea y hay uno con `fifoMatch === true`, lo elige.
 * 3. Si matchea, ejecuta `interpretEvent(payload)` (o el default).
 * 4. Invalida queries, muestra toast, y si el outcome es terminal,
 *    dismissea el toast persistente y resuelve el workflow del store.
 *
 * Montar UNA SOLA VEZ a nivel raíz, dentro del QueryClientProvider.
 */
export const usePendingWorkflowsListener = () => {
  const messages = useAuthMessages();
  const queryClient = useQueryClient();
  const pending = usePendingWorkflowsStore.use.pending();
  const resolve = usePendingWorkflowsStore.use.resolve();
  const { toast, dismiss } = useToast();

  // Evita procesar el mismo notificationId dos veces.
  const processedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    for (const message of messages) {
      const notificationId = message?.notificationId;
      if (!notificationId || processedIds.current.has(notificationId)) continue;

      const notification = message?.notification;
      const type = notification?.type;
      const payload = notification?.payload;

      if (!type || !payload) continue;

      // Filtrar workflows del mismo tipo de evento (puede ser string o array)
      const sameTypeWorkflows = Object.values(pending).filter((wf) => {
        const types = Array.isArray(wf.socketEventType)
          ? wf.socketEventType
          : [wf.socketEventType];
        return types.includes(type);
      });
      if (sameTypeWorkflows.length === 0) continue;

      // Match por correlationId primero
      let matching = sameTypeWorkflows.find((wf) =>
        payloadContainsAny(payload, wf.correlationIds)
      );

      // Fallback FIFO si nadie matcheó por correlationId
      if (!matching) {
        matching = sameTypeWorkflows
          .filter((wf) => wf.fifoMatch)
          .sort((a, b) => a.startedAt - b.startedAt)[0];
      }

      if (!matching) {
        continue;
      }

      processedIds.current.add(notificationId);

      // Interpretar el evento
      const interpret = matching.interpretEvent ?? defaultInterpretEvent;
      const outcome = interpret(payload, type);

      // Invalidar queries (las del outcome si las define, o las default).
      // refetchInactive: true asegura que las queries refresquen aunque
      // el usuario no esté actualmente en la pantalla afectada.
      const queryKeys =
        outcome.queryKeysToInvalidate ?? matching.queryKeysToInvalidate;
      for (const queryKey of queryKeys) {
        queryClient.invalidateQueries(queryKey, {
          refetchActive: true,
          refetchInactive: true,
        });
      }

      // Toast del outcome
      if (outcome.toast) {
        toast({
          title: outcome.toast.title,
          description: outcome.toast.description,
          variant: outcome.toast.variant,
          duration: outcome.toast.duration ?? 4000,
        });
      }

      // Si es terminal, cerrar todo
      if (outcome.isTerminal) {
        if (matching.acceptedToastId) dismiss(matching.acceptedToastId);
        resolve(matching.id);
      }
    }
  }, [messages, pending, queryClient, resolve, toast, dismiss]);
};
